import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiErrorResponse } from "../models/ApiResponse";
import {
	PersonFileTemplate,
	PreviewPersonFileTemplateRequest,
	PreviewPersonFileTemplateResponse,
	SavePersonFileTemplateRequest,
	SavePersonFileTemplateResponse,
} from "../models/PersonFileTemplate";
import { apiStore } from "../stores/api.store";

/**
 * oo-hogia-hr-service endpoints. Only the person-file template lives here so
 * far — the Hogia salary export itself goes through job-service.
 */
const PERSON_FILE_TEMPLATE_QUERY_KEY = "hogia-person-file-template";
const PERSON_FILE_TYPES_QUERY_KEY = "hogia-person-file-template-types";

/**
 * The organisation's person-file generator script. When `isDefault` is true
 * nothing is stored and `source` is Origo's built-in default, shown in the
 * editor as the starting point.
 */
export function usePersonFileTemplate(enabled = true) {
	const { data, ...rest } = useQuery(
		[PERSON_FILE_TEMPLATE_QUERY_KEY],
		() =>
			apiStore
				.get<PersonFileTemplate>({
					path: `/hogia/person-file-template`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{ enabled, staleTime: 30_000 }
	);

	return { template: data, ...rest };
}

/**
 * The `PersonFileContext` type definitions the script is written against,
 * verbatim from hogia-hr-service (with Swedish JSDoc per field), fed to Monaco
 * for type checking, autocomplete and hover docs.
 */
export function usePersonFileTemplateTypes(enabled = true) {
	const { data, ...rest } = useQuery(
		[PERSON_FILE_TYPES_QUERY_KEY],
		() =>
			apiStore
				.get<{ types: string }>({
					path: `/hogia/person-file-template/types`,
					isAuthRequired: true,
				})
				.then((d) => d.data.types),
		{ enabled, staleTime: Infinity }
	);

	return { types: data, ...rest };
}

/**
 * Stores the script. The backend compiles and test-runs it first, so a broken
 * script (or one producing characters outside ISO-8859-1) is rejected with a
 * 400 whose Swedish, line-numbered message is shown verbatim to the author.
 * `warnings` are advisory only.
 */
export function useSavePersonFileTemplate() {
	const queryClient = useQueryClient();

	return useMutation<
		SavePersonFileTemplateResponse,
		ApiErrorResponse,
		SavePersonFileTemplateRequest
	>(
		(body) =>
			apiStore
				.put<SavePersonFileTemplateResponse>({
					path: `/hogia/person-file-template`,
					body,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{ onSuccess: () => queryClient.invalidateQueries([PERSON_FILE_TEMPLATE_QUERY_KEY]) }
	);
}

/** Drops the org's own script so the built-in default is used again. */
export function useResetPersonFileTemplate() {
	const queryClient = useQueryClient();

	return useMutation<unknown, ApiErrorResponse, void>(
		() =>
			apiStore
				.delete({ path: `/hogia/person-file-template`, isAuthRequired: true })
				.then((d) => d.data),
		{ onSuccess: () => queryClient.invalidateQueries([PERSON_FILE_TEMPLATE_QUERY_KEY]) }
	);
}

/**
 * Renders an unsaved draft for one consultant. A mutation rather than a query
 * so the editor can debounce it and drop out-of-order responses itself.
 */
export function usePreviewPersonFileTemplate() {
	return useMutation<
		PreviewPersonFileTemplateResponse,
		ApiErrorResponse,
		PreviewPersonFileTemplateRequest
	>((body) =>
		apiStore
			.post<PreviewPersonFileTemplateResponse>({
				path: `/hogia/person-file-template/preview`,
				body,
				isAuthRequired: true,
			})
			.then((d) => d.data)
	);
}

/** Readable message from a hogia-hr-service error response. */
export function personFileTemplateErrorMessage(
	e: ApiErrorResponse | undefined,
	fallback = "Något gick fel"
) {
	return e?.error?.detail || e?.error?.title || fallback;
}
